<template>
  <div :class="{ 'flights-plan-creator': true, 'is-loading': !loadedFlights, 'is-saving': savingSchedule }">
    <template v-if="tripId">
      <d-row class="mt-5 mb-4 ml-0 mr-0 w-100">
        <h3 class="mb-0 mt-0">
          Trip schedule
          <small
            v-if="hasUnsavedChanges"
            class="text-danger unsaved-changes">
            You have unsaved changes
          </small>
        </h3>

        <d-button
          theme="primary"
          class="ml-auto"
          outline
          @click.prevent="addAirportHandler">
          Add handler
        </d-button>

        <d-button
          theme="success"
          outline
          class="ml-2"
          @click.prevent="addFlight('start')">
          Add a flight at the beginning
        </d-button>

        <d-button
          theme="success"
          outline
          class="ml-2"
          @click.prevent="addFlight('end')">
          Add flight at the end
        </d-button>

        <d-button
          theme="success"
          class="ml-2"
          @click.prevent="saveSchedule">
          Save schedule
        </d-button>
      </d-row>

      <table class="mt-4 mb-0 table table-bordered">
        <thead>
          <tr>
            <th class="text-center" style="width: 180px;">Handler</th>
            <th class="text-center" style="width: 120px;">ETA</th>
            <th class="text-center" style="width: 170px;">Slot</th>
            <th class="text-center" style="width: 240px;">Location</th>
            <th class="text-center" style="width: 170px;">Slot</th>
            <th class="text-center" style="width: 120px;">ETD</th>
            <th class="text-center" style="width: 180px;">Handler</th>
          </tr>
        </thead>
      </table>

      <div
        v-for="(flight, index) of flights"
        :key="'flight-item-' + index"
        class="flight"
        :data-index="index">
        <div :class="{
          'flight-arrival-handler': true,
          'text-center': true,
          'is-confirmed': flight.arrival_handler_confirmed && !flight.arrival_handler_requested,
          'is-required': !flight.arrival_handler_confirmed && flight.arrival_handler_requested,
          'is-mixed': flight.arrival_handler_confirmed && flight.arrival_handler_requested
        }">
          <small v-if="!flight.arrival_airport_id">Please select an arrival airport</small>
          <v-select
            v-if="flight.arrival_airport_id"
            :options="getHandlersForAirport(flight.arrival_airport_id)"
            v-model="flight.arrival_handler_id"
            :reduce="handler => handler.id"
            placeholder="Select a handler"
            label="name">
            <template slot="no-options">
              <template>There are no handlers for the provided phrase&hellip;</template>
            </template>
            <template slot="option" slot-scope="option">
              <div class="d-center">
                <template v-if="handlerIsPreferredByCustomer(flight.arrival_airport_id, option.id)">
                  {{ option.name }} <i class="material-icons">verified</i>
                </template>
                <template v-else>
                  {{ option.name }}
                </template>
              </div>
            </template>
            <template slot="selected-option" slot-scope="option">
              <div class="selected d-center">
                <template v-if="handlerIsPreferredByCustomer(flight.arrival_airport_id, option.id)">
                  {{ option.name }} <i class="material-icons">verified</i>
                </template>
                <template v-else>
                  {{ option.name }}
                </template>
              </div>
            </template>
          </v-select>
        </div>
        <div :class="{
          'flight-arrival-date': true,
          'is-invalid': flight.errors.indexOf('arrival_date') > -1 || flight.errors.indexOf('arrival_time') > -1
        }">
          <d-datepicker
            v-model="flight.arrival_date"
            format="dMMMyy"
            placeholder="Select a date"
            @input="resetError('arrival_date', index); resetError('arrival_time', index);" />
          <hour
            v-model="flight.arrival_time"
            @input="resetError('arrival_time', index); resetError('arrival_date', index);" />
        </div>
        <div
          :id="'flight-arrival-slot-' + index"
          :class="{
            'flight-arrival-slot': true,
            'not-required': flight.arrival_airport && !flight.arrival_airport.slot_required
          }"
          :data-slot-status="slotStatus(flight, 'arrival')">
          <small v-if="flight.slot_arrival_granted_date && flight.slot_arrival_granted_time">
            {{ flight.slot_arrival_granted_date | displayDate }} {{ flight.slot_arrival_granted_time.substr(0, 2) }} {{ flight.slot_arrival_granted_time.substr(2, 2) }}
          </small>
        </div>
        <div :class="{ 'flight-departure-airport': true, 'is-invalid': flight.errors.indexOf('departure_airport_id') > -1 }">
          <v-select
            :options="airports"
            v-model="flight.departure_airport_id"
            :reduce="airport => airport.id"
            :filterBy="filterByAirports"
            placeholder="Select an airport"
            label="id"
            :maxItemsDisplay="100"
            @input="updatedDepartureAirport(index); resetError('departure_airport_id', index);">
            <template slot="no-options">
              <template>There are no airports for the provided phrase&hellip;</template>
            </template>
            <template slot="option" slot-scope="option">
              <div class="d-center">
                {{ option.icao }} ({{ option.name }})
              </div>
            </template>
            <template slot="selected-option" slot-scope="option">
              <div class="selected d-center">
                {{ option.icao }} ({{ option.name }})
              </div>
            </template>
          </v-select>
        </div>
        <div
          :class="{
            'flight-departure-slot': true,
            'not-required': flight.departure_airport && !flight.departure_airport.slot_required
          }"
          :id="'flight-departure-slot-' + index"
          :data-slot-status="slotStatus(flight, 'departure')">
          <small v-if="flight.slot_departure_granted_date && flight.slot_departure_granted_time">
            {{ flight.slot_departure_granted_date | displayDate }} {{ flight.slot_departure_granted_time.substr(0, 2) }} {{ flight.slot_departure_granted_time.substr(2, 2) }}
          </small>
        </div>
        <div :class="{
          'flight-departure-date': true,
          'is-invalid': flight.errors.indexOf('departure_date') > -1 || flight.errors.indexOf('departure_time') > -1
        }">
          <d-datepicker
            v-model="flight.departure_date"
            format="dMMMyy"
            placeholder="Select a date"
            @input="resetError('departure_date', index); resetError('departure_time', index)" />
          <hour
            v-model="flight.departure_time"
            @input="resetError('departure_time', index); resetError('departure_date', index);" />
        </div>
        <div :class="{
          'flight-departure-handler': true,
          'text-center': true,
          'is-confirmed': flight.departure_handler_confirmed && !flight.departure_handler_requested,
          'is-required': !flight.departure_handler_confirmed && flight.departure_handler_requested,
          'is-mixed': flight.departure_handler_confirmed && flight.departure_handler_requested
        }">
          <small v-if="!flight.departure_airport_id">Please select a departure airport</small>
          <v-select
            v-if="flight.departure_airport_id"
            :options="getHandlersForAirport(flight.departure_airport_id)"
            v-model="flight.departure_handler_id"
            :reduce="handler => handler.id"
            placeholder="Select a handler"
            label="name">
            <template slot="no-options">
              <template>There are no handlers for the provided phrase&hellip;</template>
            </template>
            <template slot="option" slot-scope="option">
              <div class="d-center">
                <template v-if="handlerIsPreferredByCustomer(flight.departure_airport_id, option.id)">
                  {{ option.name }} <i class="material-icons">verified</i>
                </template>
                <template v-else>
                  {{ option.name }}
                </template>
              </div>
            </template>
            <template slot="selected-option" slot-scope="option">
              <div class="selected d-center">
                <template v-if="handlerIsPreferredByCustomer(flight.departure_airport_id, option.id)">
                  {{ option.name }} <i class="material-icons">verified</i>
                </template>
                <template v-else>
                  {{ option.name }}
                </template>
              </div>
            </template>
          </v-select>
        </div>
        <div
          v-if="index === flights.length - 1"
          :class="{ 'flight-arrival-airport': true, 'is-invalid': flight.errors.indexOf('arrival_airport_id') > -1 }">
          <v-select
            :options="airports"
            v-model="flight.arrival_airport_id"
            :reduce="airport => airport.id"
            :filterBy="filterByAirports"
            :maxItemsDisplay="100"
            placeholder="Select an airport"
            label="id"
            @input="updatedArrivalAirport(index); resetError('arrival_airport_id', index)">
            <template slot="no-options">
              <template>There are no airports for the provided phrase&hellip;</template>
            </template>
            <template slot="option" slot-scope="option">
              <div class="d-center">
                {{ option.icao }} ({{ option.name }})
              </div>
            </template>
            <template slot="selected-option" slot-scope="option">
              <div class="selected d-center">
                {{ option.icao }} ({{ option.name }})
              </div>
            </template>
          </v-select>
        </div>
        <div class="flight-edit-ui">
          <i
            class="material-icons text-primary"
            @click="editFlight(index)">
            edit
          </i>
          <i
            @click="deleteFlight(index)"
            class="material-icons text-danger">
            delete
          </i>
        </div>
        <div class="flight-move-ui">
          <i
            v-if="index > 0"
            @click="moveUp(index)"
            class="material-icons text-secondary move-up">
            expand_less
          </i>
          <i
            v-if="index < flights.length - 1"
            @click="moveDown(index)"
            class="material-icons text-secondar move-down">
            expand_more
          </i>
        </div>
      </div>
    </template>

    <d-alert
      v-if="!tripId"
      theme="warning"
      show
      class="text-center">
      You will be able to edit flights schedule after saving a trip.
    </d-alert>
  </div>
</template>

<script>
import DateUtils from './../../utils/DateUtils.js';
import FormUtils from './../../utils/FormUtils.js';
import Hour from './../common/Hour.vue';
import DOMPurify from 'dompurify';
import Vue from 'vue';

export default {
  name: 'flights-plan-creator',
  components: {
    'hour': Hour
  },
  props: [
    'aircraftId',
    'aircraftCustomerId',
    'aircrafts',
    'airports',
    'customers',
    'handlers',
    'tripId'
  ],
  filters: {
    displayDate (value) {
      if (value) {
        let input = new Date(value);

        return [
          input.getDate(),
          DateUtils.getMonthShortName(input.getMonth() + 1),
          input.getFullYear().toString().substr(2, 2)
        ].map(n => n < 10 ? '0' + n : n).join('');
      }

      return '';
    }
  },
  computed: {
    simplifiedLoadedFlightsData () {
      return this.loadedFlightsData.map(flight => ({
        id: flight.id,
        departure_airport_id: flight.departure_airport.id,
        arrival_airport_id: flight.arrival_airport.id
      }));
    }
  },
  data () {
    return {
      flights: [],
      loadedFlightsData: [],
      hasUnsavedChanges: false,
      loadError: false,
      loadedFlights: false,
      removedFlights: [],
      savingSchedule: false
    };
  },
  watch: {
    flights: {
      handler () {
        if (this.loadedFlights) {
          this.hasUnsavedChanges = true;
        }
      },
      deep: true
    }
  },
  mounted () {
    if (this.tripId) {
      this.loadFlights();
    } else {
      this.loadedFlights = true;
    }
  },
  methods: {
    loadFlights () {
      let self = this;
      this.flights = [];

      FormUtils.loadAdditionalData(this, {
        endpoint: '/trips/flights/items',
        params: {
          pagination: 0,
          where: {
            trip_id: this.tripId
          },
          order: {
            column: 'departure_date',
            direction: 'ASC'
          }
        },
        loadedKey: 'loadedFlights',
        outputKey: 'loadedFlightsData',
        errorKey: 'loadError',
        successAction (response) {
          for (let i = 0; i < response.length; i++) {
            let flightData = {
              id: response[i].id,
              departure_airport: response[i].departure_airport,
              departure_airport_id: response[i].departure_airport_id,
              departure_date: new Date(response[i].departure_date.split(' ')[0]),
              departure_time: response[i].departure_date.split(' ')[1].replace(/\:/g, '').substr(0, 4),
              departure_handler_id: response[i].departure_handler_id,
              departure_handler_confirmed: response[i].departure_handler_confirmed,
              departure_handler_requested: response[i].departure_handler_requested,
              arrival_airport: response[i].arrival_airport,
              arrival_airport_id: response[i].arrival_airport_id,
              arrival_date: new Date(response[i].arrival_date.split(' ')[0]),
              arrival_time: response[i].arrival_date.split(' ')[1].replace(/\:/g, '').substr(0, 4),
              arrival_handler_id: response[i].arrival_handler_id,
              arrival_handler_confirmed: response[i].arrival_handler_confirmed,
              arrival_handler_requested: response[i].arrival_handler_requested,
              slot_arrival_requested_date: response[i].slot_arrival_requested_date,
              slot_arrival_requested_time: response[i].slot_arrival_requested_time,
              slot_arrival_granted_date: response[i].slot_arrival_granted_date,
              slot_arrival_granted_time: response[i].slot_arrival_granted_time,
              slot_departure_requested_date: response[i].slot_departure_requested_date,
              slot_departure_requested_time: response[i].slot_departure_requested_time,
              slot_departure_granted_date: response[i].slot_departure_granted_date,
              slot_departure_granted_time: response[i].slot_departure_granted_time,
              errors: []
            };

            if (flightData.departure_handler_id === 0) {
              flightData.departure_handler_id = null;
            }

            if (flightData.arrival_handler_id === 0) {
              flightData.arrival_handler_id = null;
            }

            self.flights.push(flightData);
          }

          setTimeout(() => {
            self.loadedFlights = true;
            self.hasUnsavedChanges = false;
          }, 0);
        }
      });
    },
    filterByAirports (option, label, search) {
      return (option.name + ' ' + option.icao).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    addFlight (position = 'start') {
      if (position === 'start') {
        let arrivalAirportID = null;

        if (this.flights.length) {
          arrivalAirportID = this.flights[0].departure_airport_id;
        }

        this.flights.unshift({
          id: 0,
          departure_airport_id: null,
          departure_date: null,
          departure_time: '0000',
          departure_handler_id: null,
          departure_handler_confirmed: 0,
          departure_handler_requested: 0,
          arrival_airport_id: arrivalAirportID,
          arrival_date: null,
          arrival_time: '0000',
          arrival_handler_id: null,
          arrival_handler_confirmed: 0,
          arrival_handler_requested: 0,
          errors: []
        });
      } else if (position === 'end') {
        let departureAirportID = null;

        if (this.flights.length) {
          departureAirportID = this.flights[this.flights.length - 1].arrival_airport_id;
        }

        this.flights.push({
          id: 0,
          departure_airport_id: departureAirportID,
          departure_date: null,
          departure_time: '0000',
          departure_handler_id: null,
          departure_handler_confirmed: 0,
          departure_handler_requested: 0,
          arrival_airport_id: null,
          arrival_date: null,
          arrival_time: '0000',
          arrival_handler_id: null,
          arrival_handler_confirmed: 0,
          arrival_handler_requested: 0,
          errors: []
        });
      }
    },
    deleteFlight (index) {
      Vue.swal({
        title: 'Do you really want to remove this flight?',
        html: '',
        icon: 'question',
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        showCancelButton: true,
        buttonsStyling: true
      }).then((result) => {
        if (result.value) {
          if (index > 0 && index !== this.flights.length - 1) {
            Vue.set(this.flights[index - 1], 'arrival_airport_id', this.flights[index + 1].departure_airport_id);
            Vue.set(this.flights[index - 1], 'arrival_date', this.flights[index + 1].departure_date);
            Vue.set(this.flights[index - 1], 'arrival_time', this.flights[index + 1].departure_time);
            Vue.set(this.flights[index - 1], 'arrival_handler_id', this.flights[index + 1].departure_handler_id);
          }

          if (this.flights[index].id > 0) {
            let removedFlight = this.flights.splice(index, 1);
            this.removedFlights.push(removedFlight[0]);
          } else {
            this.flights.splice(index, 1);
          }
        }
      }).catch((errors) => {
        console.log(errors);
      });
    },
    editFlight (index) {
      if (this.flights[index].id === 0) {
        Vue.swal({
          title: 'You cannot edit unsaved flights',
          html: 'You will be able to edit this flight after saving a schedule.',
          icon: 'warning',
          confirmButtonText: 'OK',
          buttonsStyling: true
        });
        return;
      }

      this.$router.push('/dashboard/fligths/' + parseInt(this.flights[index].id, 10));
    },
    moveUp (index) {
      if (index === 0) {
        return;
      }

      let temp = JSON.parse(JSON.stringify(this.flights[index]));
      let prevTemp = JSON.parse(JSON.stringify(this.flights[index - 1]));
      prevTemp.arrival_airport_id = temp.arrival_airport_id;
      temp.arrival_airport_id = prevTemp.departure_airport_id;
      prevTemp.arrival_handler_id = temp.arrival_handler_id;
      temp.arrival_handler_id = prevTemp.departure_handler_id;

      if (temp.departure_date !== null) {
        temp.departure_date = new Date(temp.departure_date);
      }

      if (temp.arrival_date !== null) {
        temp.arrival_date = new Date(temp.arrival_date);
      }

      if (prevTemp.departure_date !== null) {
        prevTemp.departure_date = new Date(prevTemp.departure_date);
      }

      if (prevTemp.arrival_date !== null) {
        prevTemp.arrival_date = new Date(prevTemp.arrival_date);
      }

      if (index > 1) {
        this.flights[index - 2].arrival_airport_id = temp.departure_airport_id;
      }

      if (index > 1) {
        Vue.set(this.flights[index - 2], 'arrival_handler_id', temp.departure_handler_id);
      }

      Vue.set(this.flights, index - 1, temp);
      Vue.set(this.flights, index, prevTemp);
    },
    moveDown (index) {
      if (index >= this.flights.length - 1) {
        return;
      }

      let temp = JSON.parse(JSON.stringify(this.flights[index]));
      let nextTemp = JSON.parse(JSON.stringify(this.flights[index + 1]));
      temp.arrival_airport_id = nextTemp.arrival_airport_id;
      nextTemp.arrival_airport_id = temp.departure_airport_id;
      temp.arrival_handler_id = nextTemp.arrival_handler_id;
      nextTemp.arrival_handler_id = temp.departure_handler_id;

      if (temp.departure_date !== null) {
        temp.departure_date = new Date(temp.departure_date);
      }

      if (temp.arrival_date !== null) {
        temp.arrival_date = new Date(temp.arrival_date);
      }

      if (nextTemp.departure_date !== null) {
        nextTemp.departure_date = new Date(nextTemp.departure_date);
      }

      if (nextTemp.arrival_date !== null) {
        nextTemp.arrival_date = new Date(nextTemp.arrival_date);
      }

      if (index >= 1) {
        this.flights[index - 1].arrival_airport_id = nextTemp.departure_airport_id;
      }

      if (index > 0) {
        Vue.set(this.flights[index - 1], 'arrival_handler_id', nextTemp.departure_handler_id);
      }

      Vue.set(this.flights, index, nextTemp);
      Vue.set(this.flights, index + 1, temp);
    },
    updatedDepartureAirport (index) {
      if (this.flights.length > 1 && index > 0) {
        this.flights[index - 1].arrival_airport_id = this.flights[index].departure_airport_id;
      }

      if (index > 0) {
        this.flights[index - 1].arrival_handler_id = null;
      }

      this.flights[index].departure_handler_id = null;
    },
    updatedArrivalAirport (index) {
      this.flights[index].arrival_handler_id = null;
    },
    getHandlersForAirport (airportID) {
      if (!airportID) {
        return [];
      }

      let airportIndex = this.airports.findIndex(airport => airport.id === airportID);
      let handlerIDs = this.airports[airportIndex].handlers;

      if (handlerIDs === null) {
        handlerIDs = [];
      }

      let filteredHandlers = JSON.parse(JSON.stringify(this.handlers.filter(handler => handlerIDs.indexOf(handler.id) > -1)));

      return filteredHandlers.map(handler => {
        if (handler.type === 'AGENT') {
          handler.name += ' (A)';
        } else if (handler.type === 'HANDLER') {
          handler.name += ' (H)';
        }

        return handler;
      });
    },
    handlerIsPreferredByCustomer (airportID, handlerID) {
      if (!airportID) {
        return false;
      }

      let airportHandlersOfCustomer = [];
      let customerAirportsWithHandlersIDs = [];

      if (this.aircraftCustomerId) {
        let foundedCustomer = this.customers.filter(customer => customer.id === this.aircraftCustomerId);

        if (foundedCustomer[0]) {
          airportHandlersOfCustomer = foundedCustomer[0].airport_handlers.slice();
          customerAirportsWithHandlersIDs = airportHandlersOfCustomer.map(pair => pair[0]);
          let airportIndexInCustomerAirportHandlers = customerAirportsWithHandlersIDs.indexOf(airportID);

          if (airportIndexInCustomerAirportHandlers > -1) {
            let foundedHandlerID = airportHandlersOfCustomer[airportIndexInCustomerAirportHandlers][1];

            if (foundedHandlerID === handlerID) {
              return true;
            }
          }
        }
      }

      return false;
    },
    saveSchedule () {
      for (let i = 0; i < this.flights.length; i++) {
        this.flights[i].errors = [];
      }

      let scheduleIsFilled = this.validateScheduleFields();

      if (scheduleIsFilled) {
        let scheduleIsValid = this.validateScheduleHours();

        if (scheduleIsValid && this.removedFlights.length > 0) {
          Vue.swal({
            title: 'Do you want to save schedule?',
            html: DOMPurify.sanitize('You have removed ' + this.removedFlights.length + ' previously saved flights:<br><br>' + this.generateRemovedFlightsList() + '<br>After saving a schedule you will lose their configuration.'),
            icon: 'question',
            confirmButtonText: 'OK',
            cancelButtonText: 'Cancel',
            showCancelButton: true,
            buttonsStyling: true
          }).then((result) => {
            if (result.value) {
              this.saveFlights();
            }
          });
        } else if (scheduleIsValid && this.removedFlights.length === 0) {
          this.saveFlights();
        }
      }
    },
    saveFlights () {
      this.savingSchedule = true;
      let endpointUrl = '/trips/flights/store-many';
      let formDataToSend = {
        trip_id: this.tripId,
        flights: []
      };

      for (let i = 0; i < this.flights.length; i++) {
        formDataToSend.flights.push(this.prepareFlightData(i));
      }

      let dataToCompare = JSON.parse(JSON.stringify(formDataToSend.flights));
      let flightDocumentsToCheck = [];

      for (let i = 0; i < dataToCompare.length; i++) {
        let flightIdToCheck = dataToCompare[i].id;
        let flightToCheck = dataToCompare[i];

        for (let j = 0; j < this.simplifiedLoadedFlightsData.length; j++) {
          let oldItem = this.simplifiedLoadedFlightsData[j];

          if (oldItem.id === flightIdToCheck) {
            if (
              oldItem.departure_airport_id !== flightToCheck.departure_airport_id ||
              oldItem.arrival_airport_id !== flightToCheck.arrival_airport_id
            ) {
              flightDocumentsToCheck.push([
                this.getAirportById(flightToCheck.departure_airport_id),
                '-',
                this.getAirportById(flightToCheck.arrival_airport_id)
              ].join(' '));
            }
          }
        }
      }

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: endpointUrl,
        formData: formDataToSend,
        errorTitle: 'An error occurred',
        errorText: 'The system was unable to save the schedule. Please check if there are no date conflicts with other trips and then try again.',
        successAction: () => {
          Vue.swal({
            title: 'Success',
            html: 'Schedule has been successfully saved',
            icon: 'success',
            confirmButtonText: 'OK',
            buttonsStyling: true,
            timer: 1000,
            timerProgressBar: true
          }).then(() => {
            if (flightDocumentsToCheck && flightDocumentsToCheck.length) {
              Vue.swal({
                title: '',
                html: 'Please check passport usage for flights:<br><br>' + flightDocumentsToCheck.join('<br>'),
                icon: 'warning',
                confirmButtonText: 'OK',
                buttonsStyling: true
              });
            }

            this.savingSchedule = false;
            this.hasUnsavedChanges = false;
            this.loadFlights();
            this.$bus.$emit('calendar-flights-update');
          });
        }
      });
    },
    prepareFlightData (index) {
      let dataToSend = {
        id: this.flights[index].id,
        departure_airport_id: this.flights[index].departure_airport_id,
        arrival_airport_id: this.flights[index].arrival_airport_id
      };

      if (this.flights[index].departure_handler_id) {
        dataToSend.departure_handler_id = this.flights[index].departure_handler_id;
      }

      if (this.flights[index].arrival_handler_id) {
        dataToSend.arrival_handler_id = this.flights[index].arrival_handler_id;
      }

      let departureDateTimestamp = +this.flights[index].departure_date;
      let departureTime = this.flights[index].departure_time;
      let departureDate = new Date();
      let arrivalDateTimestamp = +this.flights[index].arrival_date;
      let arrivalTime = this.flights[index].arrival_time;
      let arrivalDate = new Date();

      departureDate.setTime(departureDateTimestamp);
      departureDate.setHours(parseInt(departureTime.substr(0, 2), 10));
      departureDate.setMinutes(parseInt(departureTime.substr(2, 2), 10));
      departureDate.setSeconds(0);
      departureDate.setMilliseconds(0);

      arrivalDate.setTime(arrivalDateTimestamp);
      arrivalDate.setHours(parseInt(arrivalTime.substr(0, 2), 10));
      arrivalDate.setMinutes(parseInt(arrivalTime.substr(2, 2), 10));
      arrivalDate.setSeconds(0);
      arrivalDate.setMilliseconds(0);

      dataToSend.departure_date = [
        [departureDate.getFullYear(), departureDate.getMonth() + 1, departureDate.getDate()].map(n => n < 10 ? '0' + n : n).join('-'),
        [departureDate.getHours(), departureDate.getMinutes(), 0].map(n => n < 10 ? '0' + n : n).join(':')
      ].join(' ');

      dataToSend.arrival_date = [
        [arrivalDate.getFullYear(), arrivalDate.getMonth() + 1, arrivalDate.getDate()].map(n => n < 10 ? '0' + n : n).join('-'),
        [arrivalDate.getHours(), arrivalDate.getMinutes(), 0].map(n => n < 10 ? '0' + n : n).join(':')
      ].join(' ');

      let timeDiff = arrivalDate.getTime() - departureDate.getTime();
      timeDiff = Math.floor(timeDiff / 1000);
      timeDiff /= 60;
      let timeHours = Math.floor(timeDiff / 60);
      let timeMinutes = Math.floor(timeDiff % 60);

      if (timeMinutes < 10) {
        timeMinutes = '0' + timeMinutes;
      }

      dataToSend.route_time = timeHours + 'h ' + timeMinutes + 'm';

      return dataToSend;
    },
    validateScheduleFields () {
      let errorsCount = 0;

      for (let i = 0; i < this.flights.length; i++) {
        if (!this.flights[i].departure_airport_id && this.flights[i].errors.indexOf('departure_airport_id') === -1) {
          this.flights[i].errors.push('departure_airport_id');
          errorsCount++;
        }

        if (i === this.flights.length - 1 && !this.flights[i].arrival_airport_id && this.flights[i].errors.indexOf('arrival_airport_id') === -1) {
          this.flights[i].errors.push('arrival_airport_id');
          errorsCount++;
        }

        if (!this.flights[i].departure_date && this.flights[i].errors.indexOf('departure_date') === -1) {
          this.flights[i].errors.push('departure_date');
          errorsCount++;
        }

        if (!this.flights[i].arrival_date && this.flights[i].errors.indexOf('arrival_date') === -1) {
          this.flights[i].errors.push('arrival_date');
          errorsCount++;
        }
      }

      return errorsCount === 0;
    },
    validateScheduleHours () {
      let timestamps = [];
      let errorsCount = 0;

      for (let i = 0; i < this.flights.length; i++) {
        let departureDateTimestamp = +this.flights[i].departure_date;
        let departureTime = this.flights[i].departure_time;
        let departureDate = new Date();
        let arrivalDateTimestamp = +this.flights[i].arrival_date;
        let arrivalTime = this.flights[i].arrival_time;
        let arrivalDate = new Date();

        departureDate.setTime(departureDateTimestamp);
        departureDate.setHours(parseInt(departureTime.substr(0, 2), 10));
        departureDate.setMinutes(parseInt(departureTime.substr(2, 2), 10));
        departureDate.setSeconds(0);
        departureDate.setMilliseconds(0);

        arrivalDate.setTime(arrivalDateTimestamp);
        arrivalDate.setHours(parseInt(arrivalTime.substr(0, 2), 10));
        arrivalDate.setMinutes(parseInt(arrivalTime.substr(2, 2), 10));
        arrivalDate.setSeconds(0);
        arrivalDate.setMilliseconds(0);

        timestamps.push([departureDate.getTime(), arrivalDate.getTime()]);
      }

      for (let i = 0; i < timestamps.length; i++) {
        if (timestamps[i][0] >= timestamps[i][1]) {
          this.flights[i].errors.push('departure_time');
          this.flights[i].errors.push('departure_date');
          errorsCount++;
        }

        if (i < timestamps.length - 1 && timestamps[i][1] >= timestamps[i + 1][0]) {
          this.flights[i].errors.push('arrival_time');
          this.flights[i].errors.push('arrival_date');
          errorsCount++;
        }
      }

      return errorsCount === 0;
    },
    resetError (errorName, index) {
      let errorIndex = this.flights[index].errors.findIndex(error => error === errorName);

      if (errorIndex > -1) {
        this.flights[index].errors.splice(errorIndex, 1);
      }
    },
    getAirportById (airportID) {
      let index = this.airports.findIndex(airport => airport.id === airportID);

      if (index > -1) {
        return this.airports[index].iata;
      }

      return '???';
    },
    generateRemovedFlightsList () {
      let output = '';

      for (let i = 0; i < this.removedFlights.length; i++) {
        output += this.getAirportById(this.removedFlights[i].departure_airport_id) + ' &rarr;' + this.getAirportById(this.removedFlights[i].arrival_airport_id) + '<br>';
      }

      return output;
    },
    addAirportHandler () {
      let airportIDs = [...new Set(Array.prototype.concat.apply([], this.flights.map(flight => [flight.departure_airport_id, flight.arrival_airport_id])))];
      let airportsData = this.airports.filter(airport => airportIDs.indexOf(airport.id) > -1);
      this.$bus.$emit('show-airport-handler-popup', airportsData);
    },
    slotStatus (slotData, type) {
      if (
        type === 'departure' &&
        slotData.departure_airport &&
        slotData.departure_airport.slot_required &&
        slotData.slot_departure_requested_date &&
        slotData.slot_departure_granted_date &&
        slotData.slot_departure_requested_time &&
        slotData.slot_departure_granted_time
      ) {
        let allowedBefore = 0;
        let allowedAfter = 0;
        let requestedDate = new Date(slotData.slot_departure_requested_date);
        let grantedDate = new Date(slotData.slot_departure_granted_date);
        let requestedHours = parseInt(slotData.slot_departure_requested_time.substr(0, 2), 10);
        let requestedMinutes = parseInt(slotData.slot_departure_requested_time.substr(2, 2), 10);
        let grantedHours = parseInt(slotData.slot_departure_granted_time.substr(0, 2), 10);
        let grantedMinutes = parseInt(slotData.slot_departure_granted_time.substr(2, 2), 10);

        requestedDate.setHours(requestedHours);
        requestedDate.setMinutes(requestedMinutes);
        requestedDate.setSeconds(0);
        requestedDate.setMilliseconds(0);
        grantedDate.setHours(grantedHours);
        grantedDate.setMinutes(grantedMinutes);
        grantedDate.setSeconds(0);
        grantedDate.setMilliseconds(0);

        if (slotData.departure_airport.departure_slot_tolerance_after !== null || slotData.departure_airport.departure_slot_tolerance_after !== '') {
          allowedBefore = parseInt(slotData.departure_airport.departure_slot_tolerance_before, 10);
          allowedAfter = parseInt(slotData.departure_airport.departure_slot_tolerance_after, 10);
        }

        let slotAllowedStart = new Date(requestedDate);
        slotAllowedStart.setMinutes(requestedDate.getMinutes() - allowedBefore);
        let slotAllowedEnd = new Date(requestedDate);
        slotAllowedEnd.setMinutes(requestedDate.getMinutes() + allowedAfter);

        if (grantedDate.getTime() >= slotAllowedStart.getTime() && grantedDate.getTime() <= slotAllowedEnd.getTime()) {
          return 'success';
        }

        return 'error';
      } else if (
        type === 'arrival' &&
        slotData.arrival_airport &&
        slotData.arrival_airport.slot_required &&
        slotData.slot_arrival_requested_date &&
        slotData.slot_arrival_granted_date &&
        slotData.slot_arrival_requested_time &&
        slotData.slot_arrival_granted_time
      ) {
        let allowedBefore = 0;
        let allowedAfter = 0;
        let requestedDate = new Date(slotData.slot_arrival_requested_date);
        let grantedDate = new Date(slotData.slot_arrival_granted_date);
        let requestedHours = parseInt(slotData.slot_arrival_requested_time.substr(0, 2), 10);
        let requestedMinutes = parseInt(slotData.slot_arrival_requested_time.substr(2, 2), 10);
        let grantedHours = parseInt(slotData.slot_arrival_granted_time.substr(0, 2), 10);
        let grantedMinutes = parseInt(slotData.slot_arrival_granted_time.substr(2, 2), 10);

        requestedDate.setHours(requestedHours);
        requestedDate.setMinutes(requestedMinutes);
        requestedDate.setSeconds(0);
        requestedDate.setMilliseconds(0);
        grantedDate.setHours(grantedHours);
        grantedDate.setMinutes(grantedMinutes);
        grantedDate.setSeconds(0);
        grantedDate.setMilliseconds(0);

        if (slotData.arrival_airport.arrival_slot_tolerance_after !== null || slotData.arrival_airport.arrival_slot_tolerance_after !== '') {
          allowedBefore = parseInt(slotData.arrival_airport.arrival_slot_tolerance_before, 10);
          allowedAfter = parseInt(slotData.arrival_airport.arrival_slot_tolerance_after, 10);
        }

        let slotAllowedStart = new Date(requestedDate);
        slotAllowedStart.setMinutes(requestedDate.getMinutes() - allowedBefore);
        let slotAllowedEnd = new Date(requestedDate);
        slotAllowedEnd.setMinutes(requestedDate.getMinutes() + allowedAfter);

        if (grantedDate.getTime() >= slotAllowedStart.getTime() && grantedDate.getTime() <= slotAllowedEnd.getTime()) {
          return 'success';
        }

        return 'error';
      }

      return 'unknown';
    }
  }
}
</script>

<style lang="scss" scoped>
.flights-plan-creator {
  margin: 0 auto;
  position: relative;
  width: 1180px;

  &.is-loading,
  &.is-saving {
    &:after {
      background: rgba(255, 255, 255, .66);
      content: "";
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;
    }

    &:before {
      content: "Loading data...";
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translateX(-50%) translateY(-50%);
      z-index: 1;
    }
  }

  &.is-saving {
    &:before {
      content: "Saving schedule...";
    }
  }

  .unsaved-changes {
    font-size: 14px;
  }

  .flight {
    display: flex;
    flex-wrap: wrap;
    height: 80px;
    margin-top: -1px;
    pointer-events: none;
    position: relative;
    width: 100%;

    & + .flight {
      margin-top: -40px;
    }

    &-edit-ui {
      display: flex;
      height: 40px;
      pointer-events: auto;
      position: absolute;
      right: -90px;
      top: 0;
      width: 90px;

      i {
        align-items: center;
        cursor: pointer;
        display: none;
        height: 40px;
        font-size: 20px;
        margin: 0 0 0 15px;
        position: relative;
        top: -2px;
        width: 20px;
      }

      &:hover {
        i {
          display: flex;
        }
      }
    }

    .hour {
      display: flex;
      margin-right: 10px;
    }

    &-move-ui {
      height: 40px;
      left: -40px;
      padding-left: 10px;
      pointer-events: auto;
      position: absolute;
      top: 0;
      width: 40px;

      i {
        align-items: center;
        cursor: pointer;
        display: none;
        height: 20px;
        font-size: 20px;
        position: absolute;
        top: 0;
        width: 20px;

        &.move-up {
          top: 0;
        }

        &.move-down {
          top: 20px;
        }
      }

      &:hover {
        i {
          display: flex;
        }
      }
    }

    &-arrival-handler,
    &-arrival-date,
    &-arrival-slot,
    &-arrival-time,
    &-arrival-airport,
    &-departure-handler,
    &-departure-date,
    &-departure-slot,
    &-departure-time,
    &-departure-airport {
      align-items: center;
      border: 1px solid #dee2e6;
      border-top: none;
      border-left: none;
      display: flex;
      height: 40px;
      justify-content: center;
      pointer-events: auto;

      &.is-invalid {
        background: rgba(255, 0, 0, .075);
      }

      .v-select {
        width: 100%;
      }

      .vdp-datepicker {
        border: none;

        /deep/ .form-control {
          background: transparent;
          border: none;
          padding-left: 5px;
          padding-right: 5px;
          text-align: center;
        }
      }
    }

    &-arrival-handler {
      border-left: 1px solid #dee2e6;
      margin-top: 40px;
      width: 180px;
    }

    &-arrival-date {
      margin-top: 40px;
      width: 120px;
    }

    &-arrival-slot {
      margin-top: 40px;
      width: 170px;

      &.not-required {
        background: #f0f0f0;
      }
    }

    &-arrival-airport {
      left: 470px;
      position: absolute;
      top: 40px;
      width: 240px;
    }

    &-departure-handler {
      width: 180px;
    }

    &-departure-date {
      width: 120px;
    }

    &-departure-slot {
      width: 170px;

      &.not-required {
        background: #f0f0f0;
      }
    }

    &-departure-airport {
      width: 240px;
    }

    &[data-index="0"] {
      .flight-arrival-handler,
      .flight-arrival-date,
      .flight-arrival-slot,
      .flight-arrival-time {
        border-top: 1px solid #dee2e6;
      }

      .flight-departure-airport {
        border-left: 1px solid #dee2e6;
        margin-left: -1px;
        width: 241px;
      }
    }
  }

  .flight-arrival-slot,
  .flight-departure-slot {
    &[data-slot-status="success"] {
      background: lighten(#17c671, 40%);
    }

    &[data-slot-status="error"] {
      background: lighten(#ffb400, 40%);
    }
  }

  .flight-arrival-handler,
  .flight-departure-handler {
    &.is-confirmed {
      background: lighten(#17c671, 40%);
    }

    &.is-required {
      background: lighten(#ffb400, 40%);
    }

    &.is-mixed {
      background-image: repeating-linear-gradient(
        135deg,
        lighten(#17c671, 40%),
        lighten(#17c671, 40%) 10px,
        lighten(#ffb400, 40%) 10px,
        lighten(#ffb400, 40%) 20px
      );
    }
  }
}
</style>
